import React, { useState } from "react";
import Card from "components/card";
import { MdClose, MdFlightTakeoff } from "react-icons/md";
import { FaPlane } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

const FlightModal = ({ isOpen, onClose, onAdd, editingItem }) => {
  const [formData, setFormData] = useState({
    name: "",
    route: "",
    time: "",
    price: "",
    departureDate: ""
  });

  React.useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name || "",
        route: editingItem.route || "",
        time: editingItem.time || "",
        price: editingItem.price || "",
        departureDate: editingItem.departureDate || ""
      });
    } else {
      setFormData({ name: "", route: "", time: "", price: "", departureDate: "" });
    }
  }, [editingItem, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({ name: "", route: "", time: "", price: "", departureDate: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[500px] max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-navy-800/95 backdrop-blur-sm border border-white/20 dark:border-navy-600/50 shadow-2xl">
        <div className="bg-gradient-to-r bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-t-[20px] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3 border border-white/30">
                <FaPlane className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{editingItem ? 'Edit Flight Details' : 'Add Flight Details'}</h3>
                <p className="text-white/80 text-sm flex items-center space-x-1">
                  <HiSparkles className="h-4 w-4" />
                  <span>Domestic & International Flights</span>
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6 bg-gradient-to-br from-yellow-50/50 to-amber-50/50 dark:from-navy-800/50 dark:to-navy-700/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-white mb-2 flex items-center space-x-2">
                <MdFlightTakeoff className="h-4 w-4 text-yellow-500" />
                <span>Flight Number</span>
              </label>
              <input
                type="text"
                placeholder="e.g., AA123"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 dark:border-navy-600 p-3 focus:border-yellow-500 focus:outline-none bg-white/70 dark:bg-navy-800/70 text-slate-900 dark:text-white backdrop-blur-sm transition-all duration-200 hover:border-yellow-300"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-green-500">💰</span>
                <span>Price</span>
              </label>
              <input
                type="text"
                placeholder="e.g., $899"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-yellow-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-yellow-300"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
              <span className="text-blue-500">🗺️</span>
              <span>Route</span>
            </label>
            <input
              type="text"
              placeholder="e.g., JFK → LAX"
              value={formData.route}
              onChange={(e) => setFormData({...formData, route: e.target.value})}
              className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-yellow-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-yellow-300"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-purple-500">📅</span>
                <span>Departure Date</span>
              </label>
              <input
                type="date"
                value={formData.departureDate}
                onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-yellow-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-yellow-300"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-orange-500">⏰</span>
                <span>Departure & Arrival Time</span>
              </label>
              <input
                type="text"
                placeholder="e.g., 10:30 AM - 2:45 PM"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-yellow-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-yellow-300"
                required
              />
            </div>
          </div>
          
          <div className="flex space-x-4 pt-6">
            <button
              type="submit"
              className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 py-3 px-6 text-white font-semibold "
            >
              {editingItem ? 'Update Flight' : 'Add Flight'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl bg-slate-100 py-3 px-6 text-slate-700 font-semibold hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default FlightModal;